import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, Autocomplete, TextField, Paper, IconButton, Avatar, Modal, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { pageBoxSx, headerRowSx, leftTextSx, createButtonWrapperSx, controlsRowSx, allSelectCombinedSx, nodeDetailsSx, bulkButtonSx, avatarSx, dutyOnSx, dutyOffSx, addGuardSX, bulkModalContainerSx, bulkModalHeaderSx, bulkModalTitleSx, bulkModalCloseSx, bulkModalBodySx, bulkTemplateBoxSx, bulkFileDropSx, bulkModalFooterSx, bulkModalCancelSx, bulkModalImportSx, bulkFileIconSx, bulkSelectTextSx, bulkBrowseTextSx, bulkTemplateFieldsSx, bulkDownloadTextSx } from './guards.styles';
import editIcon from '../../assets/edit-icon.svg';
import bulkImportIcon from '../../assets/bulk-import-icon.svg';
import AlertTriangle from '../../assets/AlertTriangle.svg';
import { modalContainerSx, modalHeaderSx, modalTitleSx, modalCloseIconSx, modalContentSx, modalWarningRowSx, modalCancelButtonSx, modalDeactivateButtonSx, modalBodyTextSx } from '../node/createNode.styles';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { nodeNameSx, nodeSubTextSx, nodeCitySx, activeTagSx, inactiveTagSx, powerIconSx, activeDotColor, inactiveDotColor } from '../node/operatingNodes.styles';
import { getGuards, setGuardActive } from '../../services/guardService';
import { getNodes } from '../../services/nodeService';
import { getAuthHeaders } from '../../services/authService';

// lightweight CSV parser: returns array of objects keyed by header row
function parseCSV(text: string): Array<Record<string, string>> {
    const rows: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === '"') {
            inQuotes = !inQuotes;
            cur += ch; // keep quotes for now
        } else if (ch === '\n' && !inQuotes) {
            rows.push(cur.replace(/\r$/, ''));
            cur = '';
        } else {
            cur += ch;
        }
    }
    if (cur.length) rows.push(cur.replace(/\r$/, ''));
    if (rows.length === 0) return [];
    const parseRow = (line: string) => {
        const out: string[] = [];
        let cell = '';
        let q = false;
        for (let i = 0; i < line.length; i++) {
            const c = line[i];
            if (c === '"') { q = !q; cell += c; }
            else if (c === ',' && !q) { out.push(cell.replace(/^"|"$/g, '').trim()); cell = ''; }
            else { cell += c; }
        }
        out.push(cell.replace(/^"|"$/g, '').trim());
        return out;
    };
    const header = parseRow(rows[0] || '');
    return rows.slice(1).map((r) => {
        const values = parseRow(r);
        const obj: Record<string, string> = {};
        for (let i = 0; i < header.length; i++) {
            obj[header[i]] = values[i] ?? '';
        }
        return obj;
    });
}

type Guard = {
    id: number;
    firstName: string;
    lastName: string;
    phone: string;
    assignedNode: string;
    status: 'Active' | 'Inactive';
    duty: 'On duty' | 'Off duty';
    startTime: string;
    dutyHours: string;
};

const Guards: React.FC<{ onAdd?: () => void; onEdit?: (guard: any) => void; onImported?: (message?: string) => void; notification?: { show: boolean; message?: string; severity?: 'success' | 'error' } }> = ({ onAdd, onEdit, onImported, notification }) => {
    // server-side rows
    const [rowsData, setRowsData] = useState<Guard[]>(() => []);
    const [fullRowsData, setFullRowsData] = useState<Guard[]>(() => []);
    const gridContainerRef = useRef<HTMLDivElement | null>(null);
    const [showPagination, setShowPagination] = useState(() => rowsData.length <= 10);
    const [pageSize, setPageSize] = useState<number>(10);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [importErrors, setImportErrors] = useState<Array<{ row: number; fields: Record<string, string> }>>(() => []);
    const [importErrorColumns, setImportErrorColumns] = useState<string[]>(() => []);
    const [parsedCsvRows, setParsedCsvRows] = useState<Array<Record<string,string>>>(() => []);
    const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
    const [powerState, setPowerState] = useState<Record<string, boolean>>(() => ({}));
    // node filter — Autocomplete searches nodes API
    const [nodeSearch, setNodeSearch] = useState<string>('');
    const [nodeOptions, setNodeOptions] = useState<Array<{ id: number; name: string }>>(() => []);
    const [selectedNode, setSelectedNode] = useState<{ id: number; name: string } | null>(null);
    const nodeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // status / duty — fixed options, client-side filter
    const [selectedStatusFilter, setSelectedStatusFilter] = useState<string | null>(null);
    const [selectedDutyFilter, setSelectedDutyFilter] = useState<string | null>(null);

    const togglePower = (name: string) => setPowerState((s) => ({ ...s, [name]: !s[name] }));

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [rowCount, setRowCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    // KAN-136: seed the node-filter Autocomplete with an initial list on mount so
    // the dropdown isn't empty when the admin first opens it. The existing
    // onInputChange handler keeps refining the list as the admin types.
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const resp = await getNodes(1, 50);
                if (mounted) setNodeOptions(resp.nodes.map((n: any) => ({ id: n.id, name: n.name })));
            } catch { /* ignore — empty list will simply force the user to type */ }
        })();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const apiPage = (paginationModel.page ?? 0) + 1;
                const apiPageSize = paginationModel.pageSize ?? 10;
                const activeParam = selectedStatusFilter === 'Active' ? true : selectedStatusFilter === 'Inactive' ? false : undefined;
                const resp = await getGuards(apiPage, apiPageSize, undefined, selectedNode?.id, activeParam);
                if (!mounted) return;
                const mapped: Guard[] = (resp.guards || []).map((g: any) => ({
                                        id: g.id,
                                        firstName: (g.name || '').split(' ')[0] || '',
                                        lastName: (g.name || '').split(' ').slice(1).join(' ') || '',
                                        phone: g.phone || '',
                                        assignedNode: (
                                            g.home_node_name ??
                                            (g.home_node && typeof g.home_node === 'object' ? (g.home_node.name ?? '') : (g.home_node ? String(g.home_node) : ''))
                                        ),
                                        status: g.active ? 'Active' : 'Inactive',
                                        duty: g.is_on_duty ? 'On duty' : 'Off duty',
                                        startTime: g.start_time ?? '',
                                        dutyHours: g.duty_hours != null ? String(g.duty_hours) : '',
                                        __api: g,
                                } as any));
                // client-side status/duty filter on the fetched page
                const filtered = mapped.filter((r) => {
                    if (selectedStatusFilter && r.status !== selectedStatusFilter) return false;
                    if (selectedDutyFilter && r.duty !== selectedDutyFilter) return false;
                    return true;
                });
                setFullRowsData(mapped);
                setRowsData(filtered);
                const pw: Record<string, boolean> = {};
                (resp.guards || []).forEach((g: any) => { pw[g.name ?? ''] = Boolean(g.active); });
                setPowerState(pw);
                const total = resp.meta?.total ?? resp.meta?.total_count ?? resp.meta?.count ?? 0;
                setRowCount(Number(total) || mapped.length);
            } catch (err: any) {
                console.error('Failed to load guards', err);
                window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Failed to load guards' } }));
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [paginationModel, selectedNode, selectedStatusFilter]);

    // re-apply status/duty client-side filter when selections change
    useEffect(() => {
        const filtered = fullRowsData.filter((r) => {
            if (selectedStatusFilter && r.status !== selectedStatusFilter) return false;
            if (selectedDutyFilter && r.duty !== selectedDutyFilter) return false;
            return true;
        });
        setRowsData(filtered);
    }, [selectedStatusFilter, selectedDutyFilter, fullRowsData]);

    useEffect(() => {
        const container = gridContainerRef.current;
        if (!container) return;
        const scroller = container.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null;
        if (!scroller) return;

        const check = () => {
            const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
            if (rowsData.length <= paginationModel.pageSize) {
                setShowPagination(true);
            } else {
                setShowPagination(atBottom);
            }
        };

        check();
        scroller.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);
        return () => {
            scroller.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
        };
    }, [rowsData.length, paginationModel.pageSize]);

    // Confirm deactivate modal state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState<Guard | null>(null);

    const openConfirm = (row: Guard) => {
        setConfirmTarget(row);
        setConfirmOpen(true);
    };

    const closeConfirm = () => {
        setConfirmOpen(false);
        setConfirmTarget(null);
    };

    const confirmToggle = () => {
        if (!confirmTarget) return;
        // KAN-129: derive action from current status — same flow activates or deactivates.
        const nextActive = confirmTarget.status === 'Inactive';
        (async () => {
            try {
                const guardId = confirmTarget.id;
                const resp = await setGuardActive(guardId, nextActive);
                const ok = resp?.success ?? resp?.data?.success ?? false;
                if (ok) {
                    setRowsData((r) => r.map((x) => (x.id === guardId ? { ...x, status: nextActive ? 'Active' : 'Inactive' } : x)));
                    const name = `${confirmTarget.firstName} ${confirmTarget.lastName}`;
                    setPowerState((s) => ({ ...s, [name]: nextActive }));
                    window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? (nextActive ? 'Guard activated' : 'Guard deactivated') } }));
                } else {
                    window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: resp?.message ?? (nextActive ? 'Failed to activate guard' : 'Failed to deactivate guard') } }));
                }
            } catch (err: any) {
                console.error('Failed to set guard active', err);
                window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? (nextActive ? 'Failed to activate guard' : 'Failed to deactivate guard') } }));
            } finally {
                closeConfirm();
            }
        })();
    };

    const columns: GridColDef[] = [
        {
            field: 'guard',
            headerName: 'Guard',
            flex: 1.2,
            minWidth: 220,
            sortable: false,
            renderCell: (params: GridRenderCellParams<any>) => {
                const api = params.row?.__api ?? {};
                const rawPhoto = api?.photo_url ?? api?.photoUrl ?? api?.data?.photo_url ?? null;
                const apiBase = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';
                const normalizeUrl = (u?: string | null) => {
                    if (!u) return null;
                    try {
                        const s = String(u);
                        if (s.startsWith('http://') || s.startsWith('https://')) return s;
                        return apiBase.replace(/\/$/, '') + (s.startsWith('/') ? s : `/${s}`);
                    } catch (err) {
                        return null;
                    }
                };
                const avatarUrl = normalizeUrl(rawPhoto);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* KAN-139: always render initials as children so MUI Avatar falls back
                            to them not only when the URL is missing but also when the image
                            fails to load (e.g. expired/invalid S3 presigned URL). */}
                        <Avatar sx={avatarSx} src={avatarUrl ?? undefined}>{`${params.row.firstName[0] ?? ''}${params.row.lastName[0] ?? ''}`}</Avatar>
                        <Box>
                            <Typography sx={nodeNameSx}>{params.row.firstName} {params.row.lastName}</Typography>
                        </Box>
                    </Box>
                );
            },
        },
        { field: 'phone', headerName: 'Phone', flex: 0.8, minWidth: 150, sortable: false, renderCell: (params: GridRenderCellParams<any>) => <Typography sx={nodeCitySx as any}>{params.row.phone}</Typography> },
        { field: 'assignedNode', headerName: 'Assigned node', flex: 1, minWidth: 180, sortable: false, renderCell: (params: GridRenderCellParams<any>) => <Typography sx={nodeCitySx as any}>{params.row.assignedNode}</Typography> },
        {
            field: 'status', headerName: 'Status', flex: 0.6, minWidth: 130, sortable: false, renderCell: (params: GridRenderCellParams<any>) => (
                params.value === 'Active' ? (
                    <Box component="span" sx={activeTagSx}>
                        <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '16777200px', bgcolor: activeDotColor, top: '9.25px', left: '10px', opacity: 1 }} />
                        <Box component="span" sx={{ pl: '18px' }}>{params.value}</Box>
                    </Box>
                ) : (
                    <Box component="span" sx={inactiveTagSx}>
                        <Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '16777200px', bgcolor: inactiveDotColor, top: '9.25px', left: '10px', opacity: 1 }} />
                        <Box component="span" sx={{ pl: '18px' }}>{params.value}</Box>
                    </Box>
                )
            )
        },
        {
            field: 'duty', headerName: 'Duty status', flex: 0.6, minWidth: 120, sortable: false, renderCell: (params: GridRenderCellParams<any>) => (
                params.value === 'On duty' ? (
                    <Box component="span" sx={{ ...dutyOnSx, px: 2, position: 'relative' }}>
                        <Box component="span">{params.value}</Box>
                    </Box>
                ) : (
                    <Box component="span" sx={{ ...dutyOffSx, px: 2, position: 'relative' }}>
                        <Box component="span">{params.value}</Box>
                    </Box>
                )
            )
        },
        {
            field: 'startTime',
            headerName: 'Start Time',
            flex: 0.6,
            minWidth: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams<any>) => (
                <Typography sx={nodeCitySx as any}>{params.row.startTime || '—'}</Typography>
            ),
        },
        {
            field: 'dutyHours',
            headerName: 'Duty Hours',
            flex: 0.5,
            minWidth: 110,
            sortable: false,
            renderCell: (params: GridRenderCellParams<any>) => (
                <Typography sx={nodeCitySx as any}>{params.row.dutyHours !== '' ? `${params.row.dutyHours} hrs` : '—'}</Typography>
            ),
        },
        {
            field: 'actions', headerName: 'Actions', flex: 0.6, minWidth: 140, sortable: false, renderCell: (params: GridRenderCellParams<any>) => {
        const fullName = `${params.row.firstName} ${params.row.lastName}`;
        const isInactive = params.row?.status === 'Inactive';
        // use theme token colors for active/inactive indicators
        const iconColor = isInactive ? inactiveDotColor : activeDotColor;
        // show slash overlay only when the guard is inactive
        const showSlash = isInactive;
                return (
                    <Box>
                        <IconButton
                            size="small"
                            onClick={() => { if (!isInactive) onEdit && onEdit(params.row?.__api ?? params.row); }}
                disabled={isInactive}
                aria-disabled={isInactive}
                sx={{ cursor: isInactive ? 'default' : 'pointer' }}
                        >
                            <Box
                                component="img"
                                src={editIcon}
                                alt="Edit"
                                sx={{
                                    width: '14px',
                                    height: '14px',
                    opacity: isInactive ? 0.4 : 1,
                    filter: isInactive ? 'grayscale(100%)' : 'none',
                                }}
                            />
                        </IconButton>
                        {/* KAN-129: power toggle stays enabled for inactive rows to allow reactivation. */}
                        <IconButton
                            size="small"
                            title={isInactive ? 'Activate' : 'Deactivate'}
                            onClick={() => { openConfirm(params.row); }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PowerSettingsNewIcon sx={{ color: iconColor, ...powerIconSx }} />
                                {showSlash && (
                                    <Box sx={{ position: 'absolute', width: '22px', height: '2px', bgcolor: iconColor, transform: 'rotate(45deg)' }} />
                                )}
                            </Box>
                        </IconButton>
                    </Box>
                );
            }
        },
    ];

    return (
        <Box sx={pageBoxSx}>
            <Box mb={2}>
                <Box sx={headerRowSx}>
                    <Box sx={leftTextSx}>
                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '30px' }}>Guards</Typography>
                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '19.5px', color: '#6B7280' }}>List of guards and their assignments.</Typography>
                    </Box>
                    <Box sx={{ ...createButtonWrapperSx, position: 'relative' }}>
                        <Button variant="outlined" sx={bulkButtonSx} startIcon={<Box component="img" src={bulkImportIcon} alt="Bulk import" sx={{ width: 16, height: 16 }} />} onClick={() => setBulkOpen(true)}>Bulk import from CSV</Button>
                        <Button variant="outlined" sx={addGuardSX} onClick={() => onAdd && onAdd()}>+ Add Guard</Button>
                        {notification && notification.show && (
                            (() => {
                                const isError = notification.severity === 'error';
                                return (
                                    <Box sx={{ position: 'absolute', bottom: 'calc(0%)', right: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: isError ? '#FEF2F2' : '#ECFDF3', border: `1px solid ${isError ? '#FCA5A5' : '#A9DEBF'}`, display: 'flex', alignItems: 'center', px: 2, zIndex: 1200 }}>
                                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: isError ? '#B91C1C' : '#008A2E', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                                            {isError ? (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 9v4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 17h.01" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                            ) : (
                                                <svg width="13" height="13" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 3L3 5L7 1" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '19.5px', letterSpacing: '-0.08px', color: isError ? '#7F1D1D' : 'inherit' }}>{notification.message}</Typography>
                                    </Box>
                                );
                            })()
                        )}
                    </Box>
                </Box>

                <Box sx={controlsRowSx}>
                    <Autocomplete
                        size="small"
                        sx={{ ...allSelectCombinedSx, minWidth: 180 }}
                        options={nodeOptions}
                        getOptionLabel={(o) => o.name}
                        value={selectedNode}
                        inputValue={nodeSearch}
                        onInputChange={(_e, val, reason) => {
                            if (reason === 'input') {
                                setNodeSearch(val);
                                if (nodeDebounceRef.current) clearTimeout(nodeDebounceRef.current);
                                nodeDebounceRef.current = setTimeout(async () => {
                                    try {
                                        const resp = await getNodes(1, 50, val);
                                        setNodeOptions(resp.nodes.map((n: any) => ({ id: n.id, name: n.name })));
                                    } catch { /* ignore */ }
                                }, 350);
                            }
                        }}
                        onChange={(_e, val) => {
                            setSelectedNode(val);
                            setNodeSearch(val?.name ?? '');
                            setPaginationModel(m => ({ ...m, page: 0 }));
                        }}
                        renderInput={(params) => <TextField {...params} placeholder="Search node..." />}
                        filterOptions={(x) => x}
                        clearOnEscape
                    />

                    <Autocomplete
                        size="small"
                        sx={{ ...allSelectCombinedSx, minWidth: 160 }}
                        options={['Active', 'Inactive']}
                        value={selectedStatusFilter}
                        onChange={(_e, val) => setSelectedStatusFilter(val)}
                        renderInput={(params) => <TextField {...params} placeholder="All Statuses" />}
                        clearOnEscape
                    />

                    <Autocomplete
                        size="small"
                        sx={{ ...allSelectCombinedSx, minWidth: 160 }}
                        options={['On duty', 'Off duty']}
                        value={selectedDutyFilter}
                        onChange={(_e, val) => setSelectedDutyFilter(val)}
                        renderInput={(params) => <TextField {...params} placeholder="All Duties" />}
                        clearOnEscape
                    />
                </Box>
            </Box>

            <Paper sx={nodeDetailsSx}>
                <div ref={gridContainerRef} style={{ height: '100%', width: '100%' }}>
                    <DataGrid
                        rows={rowsData}
                        columns={columns}
                        columnHeaderHeight={30}
                        rowHeight={45}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={(model: { page: number; pageSize: number }) => {
                            setPaginationModel(model);
                            setPageSize(model.pageSize);
                        }}
                        pageSizeOptions={[5, 10, 25, 50]}
                        rowCount={rowCount}
                        loading={loading}
                        density="comfortable"
                        disableRowSelectionOnClick
                        sx={{
                            '& .MuiDataGrid-root': { position: 'relative' },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#F9FAFB !important',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                minHeight: '40px !important',
                                maxHeight: '40px !important',
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                maxHeight: 'calc(100vh - 260px)',
                                overflow: 'auto',
                                paddingBottom: '56px',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 4,
                                background: '#FFFFFF',
                                borderTop: '1px solid rgba(0,0,0,0.06)'
                            },
                            '& .MuiDataGrid-columnHeader': {
                                backgroundColor: '#F9FAFB !important',
                                minHeight: '40px !important',
                                maxHeight: '40px !important',
                                display: 'flex',
                                alignItems: 'center',
                                borderRight: 'none',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontFamily: 'Inter, Arial, sans-serif',
                                fontWeight: 600,
                                fontSize: '11px',
                                lineHeight: '16.5px',
                                letterSpacing: '0.06px',
                            },
                            '& .MuiDataGrid-columnSeparator': { display: 'none' },
                            '& .MuiDataGrid-cell': { borderRight: 'none' },
                            '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                        }}
                    />
                </div>
            </Paper>

            {/* Bulk import modal */}
            {bulkOpen && (
                <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300 }}>
                    <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.3)' }} onClick={() => setBulkOpen(false)} />
                    <Box sx={bulkModalContainerSx}>
                        <Box sx={bulkModalHeaderSx}>
                            <Typography sx={bulkModalTitleSx}>Bulk import guards</Typography>
                            <IconButton sx={bulkModalCloseSx} onClick={() => setBulkOpen(false)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </IconButton>
                        </Box>
                        <Box sx={bulkModalBodySx}>
                            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '20.8px', color: '#111827' }}>Upload a CSV with guard details to create multiple guards at once.</Typography>
                            <Box sx={bulkTemplateBoxSx}>
                                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '13px', color: '#39A9B1' }}>CSV template fields:</Typography>
                                <Typography sx={bulkTemplateFieldsSx}>name, phone, node</Typography>
                                <Typography sx={bulkDownloadTextSx}>Download CSV template</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <label htmlFor="guard-csv-input">
                                    <input id="guard-csv-input" type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => {
                                        const f = e.target.files && e.target.files[0];
                                        if (f) {
                                            const isCsv = (f.type && f.type.indexOf('csv') !== -1) || f.name.toLowerCase().endsWith('.csv');
                                            if (!isCsv) {
                                                window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: 'Please select a CSV file' } }));
                                                setSelectedFileName(null);
                                                setSelectedFile(null);
                                                return;
                                            }
                                            setSelectedFileName(f.name);
                                            setSelectedFile(f);
                                        } else {
                                            setSelectedFileName(null);
                                            setSelectedFile(null);
                                        }
                                    }} />
                                    <Box sx={bulkFileDropSx}>
                                        <Box sx={bulkFileIconSx}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M12 4v8" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M8 8l4-4 4 4" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M21 15v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        </Box>
                                        <Typography sx={bulkSelectTextSx}>Select CSV file</Typography>
                                        <Typography sx={bulkBrowseTextSx}>{selectedFileName ?? 'Click to browse'}</Typography>
                                    </Box>
                                </label>
                            </Box>

                        </Box>
                        <Box sx={bulkModalFooterSx}>
                            <Button sx={bulkModalCancelSx} onClick={() => setBulkOpen(false)}>Cancel</Button>
                            <Button disabled={uploading || !selectedFile} sx={bulkModalImportSx} onClick={async () => {
                                if (!selectedFile) {
                                    window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: 'Please select a CSV file to import' } }));
                                    return;
                                }
                                setUploading(true);
                                try {
                                    const HOSTNAME = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';
                                    const url = `${HOSTNAME}/adminportal/guards/import`;
                                    const fd = new FormData();
                                    fd.append('file', selectedFile, selectedFile.name);
                                    const res = await fetch(url, { method: 'POST', headers: { ...getAuthHeaders() }, body: fd });
                                    const body = await res.json().catch(() => null);
                                    if (!res.ok) {
                                        // prefer an explicit invalid row count from the backend when present
                                        let invalidCount = body?.invalid_rows ?? body?.invalidRows ?? body?.invalid_count ?? body?.invalidRowsCount ?? null;
                                        // backend may return an array of row-level errors under `error` key
                                        if ((invalidCount === null || typeof invalidCount === 'undefined') && Array.isArray(body?.error)) {
                                            invalidCount = (body.error || []).length;
                                        }
                                        let msg: string;
                                        if (typeof invalidCount === 'number' && invalidCount > 0) {
                                            msg = `Validation failed, ${invalidCount} rows required data correction`;
                                        } else {
                                            msg = body?.message ?? await res.text().catch(() => 'Import failed');
                                        }
                                        window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } }));
                                    } else {
                                        // even if HTTP 200, the backend may indicate row-level validation failure
                                        let invalidCount = body?.invalid_rows ?? body?.invalidRows ?? body?.invalid_count ?? body?.invalidRowsCount ?? null;
                                        // if backend returned per-row errors array, use its length
                                        if ((invalidCount === null || typeof invalidCount === 'undefined') && Array.isArray(body?.error)) {
                                            invalidCount = (body.error || []).length;
                                        }
                                        const successFlag = typeof body?.success !== 'undefined' ? Boolean(body?.success) : true;
                                        const statusFlag = body?.status ? String(body.status).toLowerCase() : null;
                                        const rawMsg = body?.message ?? `Guards imported from ${selectedFile.name}`;

                                        // heuristics to determine if this should be treated as an error
                                        const looksLikeValidationError = (typeof invalidCount === 'number' && invalidCount > 0)
                                            || successFlag === false
                                            || statusFlag === 'failed'
                                            || /validation failed/i.test(String(rawMsg))
                                            || Array.isArray(body?.error)
                                            || (body?.error === true || typeof body?.error === 'string');

                                        if (looksLikeValidationError) {
                                            let msg: string;
                                            if (typeof invalidCount === 'number' && invalidCount > 0) {
                                                msg = `Validation failed, ${invalidCount} rows required data correction`;
                                            } else if (/validation failed/i.test(String(rawMsg))) {
                                                // try to extract a number from the message text
                                                const m = String(rawMsg).match(/(\d+)/);
                                                if (m) {
                                                    msg = `Validation failed, ${m[0]} rows required data correction`;
                                                } else {
                                                    msg = String(rawMsg);
                                                }
                                            } else {
                                                msg = String(rawMsg);
                                            }
                                            // if backend provided a detailed error array, show it in a modal table
                                            if (Array.isArray(body?.error) && body.error.length > 0) {
                                                // build per-row fields map from backend shape: { row, fields: [{ field, error }] }
                                                const rowsMap = new Map<number, Record<string, string>>();
                                                const allFields = new Set<string>();
                                                (body.error || []).forEach((e: any) => {
                                                    const r = Number(e.row ?? e.row_number ?? e.r ?? 0) || 0;
                                                    if (!rowsMap.has(r)) rowsMap.set(r, {});
                                                    const fieldsArr = Array.isArray(e.fields) ? e.fields : (e.field ? [e] : []);
                                                    fieldsArr.forEach((f: any) => {
                                                        const fname = String(f.field ?? f.column ?? '').trim();
                                                        if (!fname) return;
                                                        allFields.add(fname);
                                                        rowsMap.get(r)![fname] = String(f.error ?? f.message ?? 'Invalid');
                                                    });
                                                });
                                                const rowsArr = Array.from(rowsMap.entries()).map(([row, fields]) => ({ row, fields }));
                                                // prioritize common CSV columns
                                                const preferred = ['name', 'phone', 'node'];
                                                const otherFields = Array.from(allFields).filter((f) => !preferred.includes(f));
                                                setImportErrorColumns([...preferred.filter((p) => allFields.has(p)), ...otherFields]);
                                                setImportErrors(rowsArr);
                                                setErrorModalOpen(true);
                                                // if we have the selected CSV file, attempt to parse it to show values alongside errors
                                                if (selectedFile) {
                                                    try {
                                                        const text = await selectedFile.text();
                                                        const parsed = parseCSV(text);
                                                        setParsedCsvRows(parsed);
                                                    } catch (_) {
                                                        setParsedCsvRows([]);
                                                    }
                                                }
                                            }
                                            window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } }));
                                        } else {
                                            const msg = rawMsg;
                                            if (onImported) onImported(msg);
                                            window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: msg } }));
                                            setBulkOpen(false);
                                        }
                                    }
                                } catch (err: any) {
                                    console.error('Import failed', err);
                                    window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? 'Import failed' } }));
                                } finally {
                                    setUploading(false);
                                }
                            }}>Import guards</Button>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Confirm Activate/Deactivate modal — KAN-129 */}
            <Modal open={confirmOpen} onClose={closeConfirm}>
                <Box sx={modalContainerSx}>
                    <Box sx={modalHeaderSx}>
                        <Typography sx={modalTitleSx}>{confirmTarget?.status === 'Inactive' ? 'Activate guard?' : 'Deactivate guard?'}</Typography>
                        <IconButton size="small" onClick={closeConfirm} sx={modalCloseIconSx}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </IconButton>
                    </Box>
                    <Box sx={modalContentSx}>
                        <Box sx={modalWarningRowSx}>
                            <Box sx={{
                                width: '80px',
                                height: '36px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                backgroundColor: '#FEF2F2',
                                opacity: 1,
                                boxSizing: 'border-box',
                                overflow: 'hidden',
                                p: 0,
                            }}>
                                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px', opacity: 1, display: 'block', objectFit: 'contain' }} />
                            </Box>
                            <Box>
                                <Typography sx={modalBodyTextSx}>{confirmTarget?.status === 'Inactive' ? 'Activating this guard will restore their access to the Responder App.' : 'Deactivated guards cannot log in to the Responder App.'}</Typography>
                            </Box>
                        </Box>
                        <Box display="flex" justifyContent="flex-end" gap={2}>
                            <Button sx={modalCancelButtonSx} onClick={closeConfirm}>Cancel</Button>
                            <Button sx={modalDeactivateButtonSx} onClick={() => { confirmToggle(); }}>{confirmTarget?.status === 'Inactive' ? 'Activate' : 'Deactivate'}</Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
            {/* Import Errors modal */}
            <Modal open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
                <Box sx={{ position: 'absolute', top: '6%', left: '50%', transform: 'translateX(-50%)', width: '80%', maxHeight: '84%', bgcolor: '#FFFFFF', borderRadius: 2, p: 3, overflow: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>Bulk import validation errors</Typography>
                        <IconButton size="small" onClick={() => setErrorModalOpen(false)}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </IconButton>
                    </Box>
                    <Typography sx={{ mb: 2, color: '#6B7280' }}>The import failed validation. Please correct the rows below and try again.</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Row</TableCell>
                                {(importErrorColumns.length ? importErrorColumns : ['name','phone','node']).map((col) => (
                                    <TableCell key={col} sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{col}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {importErrors.map((e, idx) => {
                                const parsed = parsedCsvRows[(e.row || 1) - 1] ?? {};
                                return (
                                    <TableRow key={idx}>
                                        <TableCell>{e.row}</TableCell>
                                        {(importErrorColumns.length ? importErrorColumns : ['name','phone','node']).map((col) => (
                                            <TableCell key={col} sx={{ verticalAlign: 'top', py: 0.5, px: 1 }}>
                                                <div style={{ display: 'ruby', flexDirection: 'column' }}>
                                                    {e.fields && e.fields[col] ? (
                                                        <>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                                                <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 0 }}>{parsed[col] ?? ''}</div>
                                                                <div style={{ width: 16, height: 16, marginLeft: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke="#B91C1C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                                                </div>
                                                            </div>
                                                            <div style={{ color: '#B91C1C', fontSize: '12px', marginTop: 4 }}>{e.fields[col]}</div>
                                                        </>
                                                    ) : (
                                                        <div style={{ height: 20 }} />
                                                    )}
                                                </div>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Button variant="outlined" onClick={() => { setErrorModalOpen(false); setImportErrors([]); }}>Close</Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default Guards;
